import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { decodeJwt, formatCurrency } from '../utils';
import api from '../services/api';
import { appointmentService, Appointment } from '../services/appointmentService';

type CustomerTab = 'Mis Pedidos' | 'Mis Reservas' | 'Detalles de Cuenta';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<CustomerTab>('Mis Pedidos');
  const [orders, setOrders] = useState<Array<{
    id: number;
    created_at: string;
    status: string;
    total_amount: string | number;
  }>>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [ordersFetched, setOrdersFetched] = useState(false);
  const [bookings, setBookings] = useState<Appointment[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState<string | null>(null);
  const [bookingsFetched, setBookingsFetched] = useState(false);

  // Ruta protegida: redirigir si no está autenticado
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  // Cargar pedidos cuando la pestaña "Mis Pedidos" esté activa (solo una vez)
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setOrdersLoading(true);
        setOrdersError(null);
        const response = await api.get('orders/orders/');
        const data = Array.isArray(response.data)
          ? response.data
          : response.data?.results || response.data || [];
        setOrders(data);
        setOrdersFetched(true);
      } catch (error: any) {
        const message =
          error?.response?.data?.detail ||
          error?.message ||
          'No se pudieron cargar los pedidos.';
        setOrdersError(message);
      } finally {
        setOrdersLoading(false);
      }
    };

    if (!loading && isAuthenticated && activeTab === 'Mis Pedidos' && !ordersFetched) {
      fetchOrders();
    }
  }, [activeTab, isAuthenticated, loading, ordersFetched]);

  // Cargar reservas cuando la pestaña "Mis Reservas" esté activa (solo una vez)
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setBookingsLoading(true);
        setBookingsError(null);
        const data = await appointmentService.getAllBookings();
        const filtered = user?.email ? data.filter((b) => b.customer_email === user.email) : data;
        setBookings(filtered);
        setBookingsFetched(true);
      } catch (error: any) {
        const message =
          error?.response?.data?.detail ||
          error?.message ||
          'No se pudieron cargar las reservas.';
        setBookingsError(message);
      } finally {
        setBookingsLoading(false);
      }
    };

    if (!loading && isAuthenticated && activeTab === 'Mis Reservas' && !bookingsFetched) {
      fetchBookings();
    }
  }, [activeTab, isAuthenticated, loading, bookingsFetched, user]);

  // Derivar rol desde token (fallback a CUSTOMER)
  const role: 'ADMIN' | 'CUSTOMER' = useMemo(() => {
    try {
      const token = localStorage.getItem('access_token');
      const claims = token ? decodeJwt(token) : null;
      const isStaff = claims?.is_staff === true;
      return isStaff ? 'ADMIN' : 'CUSTOMER';
    } catch {
      return 'CUSTOMER';
    }
  }, []);

  const fullName = useMemo(() => {
    const first = user?.first_name || '';
    const last = user?.last_name || '';
    const name = `${first} ${last}`.trim();
    return name || user?.email || '';
  }, [user]);

  if (loading) {
    return (
      <div className="container mx-auto p-6 min-h-screen pt-[96px]" style={{ backgroundColor: '#FFFBF7' }}>
        <div className="text-center text-gray-700">Cargando perfil…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // La redirección ya manejó el flujo
  }

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: '#FFFBF7' }}>
      <div className="container mx-auto p-4 md:p-12">
        {role === 'ADMIN' ? (
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-sm p-6">
            <h1 className="text-3xl font-semibold text-gray-800 mb-2">Panel de Administrador</h1>
            <p className="text-gray-700 mb-6">Bienvenido, {fullName}. Gestiona la tienda desde tu panel.</p>
            <div className="flex items-center gap-3">
              <Link to="/admin" className="inline-block bg-pink-dark text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition-colors">
                Ir al Panel
              </Link>
              <button onClick={logout} className="inline-block px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50">
                Cerrar Sesión
              </button>
            </div>
          </div>
        ) : (
          <div>
          <h1 className="text-3xl font-semibold text-gray-800 mb-8">Dashboard de Cliente</h1>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Columna Izquierda - Menú sticky */}
            <div className="md:col-span-1">
              <div className="sticky top-24 bg-white rounded-lg shadow-sm p-4">
                <div className="flex flex-col gap-2">
                  <button
                    className={`text-left px-4 py-2 rounded-lg ${activeTab === 'Mis Pedidos' ? 'bg-pink-light text-gray-900' : 'hover:bg-pink-light'}`}
                    onClick={() => setActiveTab('Mis Pedidos')}
                  >
                    Mis Pedidos
                  </button>
                  <button
                    className={`text-left px-4 py-2 rounded-lg ${activeTab === 'Mis Reservas' ? 'bg-pink-light text-gray-900' : 'hover:bg-pink-light'}`}
                    onClick={() => setActiveTab('Mis Reservas')}
                  >
                    Mis Reservas
                  </button>
                  <button
                    className={`text-left px-4 py-2 rounded-lg ${activeTab === 'Detalles de Cuenta' ? 'bg-pink-light text-gray-900' : 'hover:bg-pink-light'}`}
                    onClick={() => setActiveTab('Detalles de Cuenta')}
                  >
                    Detalles de Cuenta
                  </button>

                  <hr className="my-3" />
                  <button onClick={logout} className="text-left px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50">
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            </div>

            {/* Columna Derecha - Contenido */}
            <div className="md:col-span-3">
              <div className="bg-white rounded-lg shadow-sm p-6 min-h-[200px]">
                {activeTab === 'Mis Pedidos' && (
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3">Mis Pedidos</h2>
                    {ordersLoading && (
                      <p className="text-gray-700">Cargando pedidos...</p>
                    )}
                    {!ordersLoading && ordersError && (
                      <p className="text-red-600">{ordersError}</p>
                    )}
                    {!ordersLoading && !ordersError && orders.length === 0 && (
                      <p className="text-gray-700">Aún no has realizado ningún pedido.</p>
                    )}
                    {!ordersLoading && !ordersError && orders.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full bg-white rounded-lg">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left px-4 py-3 text-gray-700 font-medium">Nº de Pedido</th>
                              <th className="text-left px-4 py-3 text-gray-700 font-medium">Fecha</th>
                              <th className="text-left px-4 py-3 text-gray-700 font-medium">Estado</th>
                              <th className="text-left px-4 py-3 text-gray-700 font-medium">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.map((order) => {
                              const date = new Date(order.created_at);
                              const total =
                                typeof order.total_amount === 'string'
                                  ? parseFloat(order.total_amount)
                                  : order.total_amount;
                              return (
                                <tr key={order.id} className="border-t">
                                  <td className="px-4 py-3 text-gray-800">#{order.id}</td>
                                  <td className="px-4 py-3 text-gray-800">
                                    {isNaN(date.getTime())
                                      ? '-'
                                      : date.toLocaleDateString('es-CL', {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit',
                                        })}
                                  </td>
                                  <td className="px-4 py-3 text-gray-800">{order.status || '-'}</td>
                                  <td className="px-4 py-3 text-gray-800">
                                    {Number.isFinite(total)
                                      ? formatCurrency(total as number)
                                      : '-'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'Mis Reservas' && (
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3">Mis Reservas</h2>
                    {bookingsLoading && (
                      <p className="text-gray-700">Cargando reservas...</p>
                    )}
                    {!bookingsLoading && bookingsError && (
                      <p className="text-red-600">{bookingsError}</p>
                    )}
                    {!bookingsLoading && !bookingsError && bookings.length === 0 && (
                      <p className="text-gray-700">Aún no has realizado ninguna reserva.</p>
                    )}
                    {!bookingsLoading && !bookingsError && bookings.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full bg-white rounded-lg">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left px-4 py-3 text-gray-700 font-medium">Servicio</th>
                              <th className="text-left px-4 py-3 text-gray-700 font-medium">Profesional</th>
                              <th className="text-left px-4 py-3 text-gray-700 font-medium">Fecha</th>
                              <th className="text-left px-4 py-3 text-gray-700 font-medium">Hora</th>
                              <th className="text-left px-4 py-3 text-gray-700 font-medium">Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bookings.map((b) => {
                              const dateParts = b.date.split('-');
                              const date = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));
                              const servicesNames = Array.isArray(b.appointment_services)
                                ? b.appointment_services.map((s: any) => s.name).join(', ')
                                : '-';
                              const profName = b.professional_detail
                                ? b.professional_detail.full_name || `${b.professional_detail.first_name || ''} ${b.professional_detail.last_name || ''}`.trim()
                                : 'No asignado';
                              const estado = b.professional_detail ? 'Confirmada' : 'Pendiente';
                              return (
                                <tr key={b.id} className="border-t">
                                  <td className="px-4 py-3 text-gray-800">{servicesNames || '-'}</td>
                                  <td className="px-4 py-3 text-gray-800">{profName || '-'}</td>
                                  <td className="px-4 py-3 text-gray-800">
                                    {isNaN(date.getTime())
                                      ? '-'
                                      : date.toLocaleDateString('es-CL', {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit',
                                        })}
                                  </td>
                                  <td className="px-4 py-3 text-gray-800">{b.start_time} - {b.end_time}</td>
                                  <td className="px-4 py-3 text-gray-800">{estado}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'Detalles de Cuenta' && (
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Detalles de Cuenta</h2>
                    <form className="space-y-4 max-w-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                        <input
                          type="text"
                          value={fullName}
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={user?.email || ''}
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nueva Contraseña</label>
                        <input
                          type="password"
                          placeholder="Ingresa tu nueva contraseña"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <button type="button" className="bg-pink-dark text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition-colors">
                          Guardar cambios
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
